// Portal Work — the row-level quick actions (the mobile action bar's contents).
//
// PURE DATA, NO MARKUP. Every action is described as a list of options, and
// every option carries the exact `update` object to hand `updateTask`. That is
// what lets ONE handler in the orchestrator serve all five actions instead of
// five near-identical ones, and it is what makes the whole surface testable
// without rendering anything.
//
// Order is Anthony's (2026-08-13): date, assignee, priority, AREA, then park —
// he asked for area ahead of park explicitly.
import taxonomy from '../../../canon/taxonomy.json';
import {
	AREA_ORDER,
	GATE_ORDER,
	PEOPLE_BY_UID,
	areaMeta,
	gateMeta,
	formatShortDate,
	isSameDay,
	quickDeadlineOptions,
} from './taskModel';
import { isGateOffered, isPersonOffered } from './spaceFields';

const PRIORITY_META = Object.fromEntries(
	[...taxonomy.priorities.values]
		.sort((a, b) => a.order - b.order)
		.map((p) => [p.key, { key: p.key, label: p.label }])
);
const PRIORITY_KEYS = Object.keys(PRIORITY_META);

const PRIORITY_ICON = {
	high: 'fa-flag',
	medium: 'fa-circle',
	low: 'fa-circle-small',
};

// ---- The five actions ------------------------------------------------------

export function dueOptions(task, now = new Date()) {
	const deadline = task?.deadline || null;
	const opts = quickDeadlineOptions(now).map((o) => ({
		key: o.key,
		label: o.label,
		hint: o.hint,
		current: isSameDay(deadline, o.date),
		update: { deadline: o.date },
	}));
	if (deadline) {
		opts.push({
			key: 'clear',
			label: 'Clear due date',
			icon: 'fa-xmark',
			destructive: true,
			current: false,
			update: { deadline: null },
		});
	}
	return opts;
}

// The assignee write is TWO fields, and both are required: `assigneeUids` is
// canonical and `assignedTo` is the legacy name mirror the older views still
// read. Writing one without the other leaves a task that is assigned in one
// surface and unassigned in another — the shape is copied from the existing
// handleReassign rather than reinvented.
export function assigneeOptions(task, fields) {
	const current = task?.assigneeUids || [];
	const people = Object.values(PEOPLE_BY_UID).filter((p) =>
		isPersonOffered(fields, p)
	);
	return [
		{
			key: 'unassigned',
			label: 'Unassigned',
			icon: 'fa-user-slash',
			current: current.length === 0,
			update: { assigneeUids: [], assignedTo: [] },
		},
		...people.map((p) => ({
			key: p.uid,
			label: p.name,
			initials: p.initials,
			colorVar: p.colorVar,
			current: current.length === 1 && current[0] === p.uid,
			update: { assigneeUids: [p.uid], assignedTo: [p.name] },
		})),
	];
}

export function priorityOptions(task) {
	return PRIORITY_KEYS.map((key) => ({
		key,
		label: PRIORITY_META[key].label,
		icon: PRIORITY_ICON[key] || 'fa-circle',
		current: task?.priority === key,
		update: { priority: key },
	}));
}

export function areaOptions(task, areaOrder = AREA_ORDER) {
	return [
		...areaOrder.map((key) => ({
			key,
			label: areaMeta(key).label,
			areaKey: key,
			current: task?.area === key,
			update: { area: key },
		})),
		{
			key: 'none',
			label: 'No area',
			icon: 'fa-ban',
			current: !task?.area,
			update: { area: null },
		},
	];
}

// Parking is a GATE, never a status change — the gate names what the card is
// waiting on and leaves its status alone. Unparking is the same write with null,
// which is why it belongs in this list rather than in a separate control.
export function gateOptions(task, fields, offeredGates) {
	const keys = offeredGates || GATE_ORDER.filter((g) => isGateOffered(fields, g));
	const opts = keys.map((key) => {
		const meta = gateMeta(key);
		return {
			key,
			label: meta?.label || key,
			icon: meta?.icon || 'fa-circle-pause',
			current: task?.gate === key,
			update: { gate: key },
		};
	});
	if (task?.gate) {
		opts.push({
			key: 'unpark',
			label: 'Unpark',
			icon: 'fa-play',
			current: false,
			update: { gate: null },
		});
	}
	return opts;
}

// ---- The bar itself --------------------------------------------------------

/**
 * The ordered action list the mobile bar renders.
 *
 * `summary` is what the button shows underneath its icon — the CURRENT value,
 * so the bar doubles as a read of the task's state and not only a set of
 * controls. That is the difference between a toolbar and a row of mystery
 * glyphs, and on a phone it is the only place some of these fields are visible
 * at all (the desktop columns are hidden under 768px).
 */
export function quickActions(task, ctx = {}) {
	const { now = new Date(), fields, areaOrder, offeredGates } = ctx;
	const assignees = task?.assigneeUids || [];
	const assignedName =
		assignees.length === 1 ? PEOPLE_BY_UID[assignees[0]]?.name : null;
	return [
		{
			key: 'due',
			label: 'Due date',
			icon: task?.deadline ? 'fa-calendar-day' : 'fa-calendar-plus',
			// formatShortDate ("Sep 3"), NOT the ISO value — the caption sits in a
			// ~56px button at 375px, where "2026-09-03" is pure ellipsis. It also
			// matches the date format the row itself already shows.
			summary: task?.deadline ? formatShortDate(task.deadline) : null,
			options: dueOptions(task, now),
		},
		{
			key: 'assignee',
			label: 'Assignee',
			icon: 'fa-user',
			summary:
				assignedName || (assignees.length > 1 ? `${assignees.length} people` : null),
			options: assigneeOptions(task, fields),
		},
		{
			key: 'priority',
			label: 'Priority',
			icon: 'fa-flag',
			summary: PRIORITY_META[task?.priority]?.label || null,
			options: priorityOptions(task),
		},
		{
			key: 'area',
			label: 'Area',
			icon: 'fa-layer-group',
			summary: task?.area ? areaMeta(task.area).label : null,
			options: areaOptions(task, areaOrder),
		},
		{
			key: 'gate',
			label: 'Park',
			icon: 'fa-circle-pause',
			summary: task?.gate ? gateMeta(task.gate)?.label || task.gate : null,
			options: gateOptions(task, fields, offeredGates),
		},
	];
}
